import { maskMoney } from '../../../../../../services/masks';
import { ASSISTANCE_DETAILS } from '../PottencialHire/components/AssistanceSelectCards';

export type HireSummaryData = {
  plan?: { name: string; monthlyValue?: number };
  assistance?: { name: string; monthlyValue: number };
  planPremium?: number;
  assistancePremium?: number;
  quotedPremium?: number;
  totalPremium?: number;
  installments?: { quantity: number; value: number };
  notice?: string;
};

export type QuotedValues = {
  installments: number;
  installmentValue: number;
  totalPremium: number;
};

export type NormalizedOffer = {
  name: string;
  installments: number;
  installmentValue: number;
  totalPremium: number;
};

export const VALUES_PENDING_NOTICE = 'Os valores serão confirmados pela seguradora.';

export const toNumber = (value: unknown): number => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

export const formatBRL = (value: number) => `R$ ${maskMoney(String(value), true)}`;

const LOWERCASE_WORDS = ['de', 'da', 'do', 'das', 'dos', 'e'];

// 'POTTENCIAL TRADICIONAL' -> 'Pottencial Tradicional'
export const formatPlanName = (name?: string) =>
  String(name || '')
    .toLowerCase()
    .split(' ')
    .map((word, i) =>
      i > 0 && LOWERCASE_WORDS.includes(word) ? word : word.charAt(0).toUpperCase() + word.slice(1)
    )
    .join(' ');

/**
 * Valores cotados da análise. Usa complete_values quando vierem preenchidos e, se vierem
 * zerados (seguradoras sem strategy no back), cai para o valor mensal da análise x vigência.
 */
export const resolveQuotedValues = ({
  maxInstallments,
  nominalTotal,
  analysisValue,
  validity
}: {
  maxInstallments?: unknown;
  nominalTotal?: unknown;
  analysisValue?: unknown;
  validity?: unknown;
}): QuotedValues | null => {
  const max = toNumber(maxInstallments);
  const nominal = toNumber(nominalTotal);
  if (max > 0 && nominal > 0) {
    return { installments: max, installmentValue: nominal / max, totalPremium: nominal };
  }

  const value = toNumber(analysisValue);
  const months = toNumber(validity);
  if (value > 0 && months > 0) {
    return { installments: months, installmentValue: value, totalPremium: value * months };
  }

  return null;
};

// O analysis.value do AnalysisProgressModal vem formatado; aqui pegamos o número cru da análise
export const findRawAnalysisValue = (bondInsurance: any, analysisId?: string) =>
  bondInsurance?.bond_insurance_analysis?.find((analysis: any) => analysis.id === analysisId)
    ?.value;

/**
 * Prêmio total: Pottencial/Porto mandam premioTotal; nas demais (Segimob, planos editados
 * manualmente) o que será cobrado é parcelas x valor da parcela.
 */
export const normalizeOffer = (offer: any): NormalizedOffer => {
  const installments = toNumber(offer?.quantidadeParcela);
  const installmentValue = toNumber(offer?.valorParcela);
  const explicitTotal = toNumber(offer?.premioTotal);

  return {
    name: formatPlanName(offer?.nomeSeguro),
    installments,
    installmentValue,
    totalPremium: explicitTotal > 0 ? explicitTotal : installments * installmentValue
  };
};

// O selectedPlan do payload não carrega todos os campos da oferta
export const findOffer = (offers: any[] | undefined, plan: any) => {
  if (!plan) return undefined;
  const planId = plan.id || plan.codigoOrcamento;
  return (
    offers?.find(offer => planId && (offer.id || offer.codigoOrcamento) === planId) ??
    offers?.find(offer => offer.nomeSeguro === plan.nomeSeguro)
  );
};

export const buildOfferSummary = (offer: any): HireSummaryData => {
  const normalized = normalizeOffer(offer);

  return {
    plan: { name: normalized.name, monthlyValue: normalized.installmentValue || undefined },
    totalPremium: normalized.totalPremium || undefined,
    installments:
      normalized.installments && normalized.installmentValue
        ? { quantity: normalized.installments, value: normalized.installmentValue }
        : undefined
  };
};

export const buildPottencialSummary = (
  offer: any,
  assistance: { id: string; grossAmount: number } | undefined,
  validity: number
): HireSummaryData => {
  const plan = normalizeOffer(offer);
  const assistancePremium = toNumber(assistance?.grossAmount);
  const assistanceMonthly = validity > 0 ? assistancePremium / validity : 0;

  return {
    plan: { name: plan.name, monthlyValue: plan.installmentValue },
    assistance: assistance
      ? {
          name: ASSISTANCE_DETAILS[assistance.id]?.name ?? assistance.id,
          monthlyValue: assistanceMonthly
        }
      : undefined,
    planPremium: plan.totalPremium,
    assistancePremium: assistancePremium > 0 ? assistancePremium : undefined,
    totalPremium: plan.totalPremium + assistancePremium,
    installments: {
      quantity: plan.installments,
      value: plan.installmentValue + assistanceMonthly
    }
  };
};

export const buildGeneralSummary = ({
  selectedPlan,
  offers,
  insurerName,
  hiredInstallments,
  quoted,
  hideValues
}: {
  selectedPlan?: any;
  offers?: any[];
  insurerName?: string;
  hiredInstallments?: number;
  quoted: QuotedValues | null;
  hideValues?: boolean;
}): HireSummaryData => {
  if (selectedPlan) {
    return buildOfferSummary(findOffer(offers, selectedPlan) ?? selectedPlan);
  }

  const plan = { name: insurerName || '' };
  if (hideValues || !quoted) {
    return { plan, notice: VALUES_PENDING_NOTICE };
  }

  const quantity = toNumber(hiredInstallments) || quoted.installments;
  const totalPremium = quoted.installmentValue * quantity;

  return {
    plan: { ...plan, monthlyValue: quoted.installmentValue },
    quotedPremium:
      Math.abs(quoted.totalPremium - totalPremium) > 0.01 ? quoted.totalPremium : undefined,
    totalPremium,
    installments: { quantity, value: quoted.installmentValue }
  };
};
